// In-app QR scanning (Aug 2026) — the camera-side counterpart of qrcode.js. That module
// RENDERS the quick-join QR for Settings; this one READS it, from inside the app on the
// joining device, so the scanned credentials land in the PWA's own storage context rather
// than whatever browser the OS camera app decided to open (Chrome on Android, always
// Safari on iOS — which doesn't even share localStorage with the installed app). Decoding
// uses the native BarcodeDetector API where it exists (Chrome/Android — fast, off-thread)
// and falls back to `jsQR` (MIT, cozmo, the de-facto standard pure-JS QR decoder)
// everywhere else, which in practice means iOS Safari, where BarcodeDetector still
// doesn't exist at all. Same single-importer rule as qrcode.js: this module is the ONLY
// place jsQR is imported, so a future swap touches exactly one file. No React imports —
// plain DOM + media APIs only, per the import-free-lib-modules convention.
import jsQR from "jsqr";

// Whether this device can plausibly scan at all. Used to hide the "Scan QR to join"
// button entirely rather than let it dead-end — getUserMedia only exists in secure
// contexts (https / localhost), which the app always runs in, so absence here really
// does mean "no camera path on this device".
export function cameraSupported() {
  return typeof navigator !== "undefined" && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}

// Parses ANY decoded QR text into a quick-join payload, or null if it isn't one.
//
// TWO shapes, because qrcode.js builds two (LB-341, fixed 21 Sep). joinDeepLink() carries
// `code` + `jointoken` — the standing 16-char household token behind Settings' permanent QR,
// which mints on the spot. joinTicketLink() carries `code` + `jointicket` — the one-time
// eight-character Add-a-device ticket, which does NOT mint: it parks a request the issuing
// adult approves. This parser only knew `jointoken`, so every Add-a-device QR scanned with
// the in-app scanner was answered "That's not a Little Bankers join code" — while the same
// QR scanned with the phone's own camera worked, because Root's boot had been taught both.
// The two are told apart by `kind` rather than by which key is present, so a caller cannot
// route one down the other's path by accident: they earn very different things.
//
// Applies the same validation Root.jsx applies to the URL-param variant of each flow.
// Deliberately does NOT check the URL's origin: staging QRs scanned against production (or
// vice versa, or a future custom domain) should still parse — verification against the real
// server is the actual security gate, not the hostname printed inside the QR. Which is also
// why a caller must never navigate to the SCANNED text: rebuild the address from these
// validated parts on this app's own origin.
export function parseJoinPayload(text) {
  let url;
  try {
    url = new URL(String(text || ""));
  } catch {
    return null;
  }
  const code = (url.searchParams.get("code") || "").toUpperCase();
  if (!/^[A-Z0-9]{6}$/.test(code)) return null;
  const token = url.searchParams.get("jointoken") || "";
  if (token) return { kind: "token", code, token };
  // Same shape Root.jsx:145 accepts — eight characters, optionally hyphenated for reading.
  const ticket = url.searchParams.get("jointicket") || "";
  if (/^[A-Za-z0-9-]{8,9}$/.test(ticket)) return { kind: "ticket", code, ticket };
  return null;
}

// Starts the camera on `video` and begins a decode loop, calling `onDecoded(rawText)` for
// every QR found (the CALLER decides what counts as a valid payload via parseJoinPayload
// above — this keeps "wrong QR, keep scanning" UI logic out of this module). Resolves to
// a stop() function that tears everything down: interval, camera tracks, srcObject.
// Rejects with err.kind = "denied" (permission refused) or "unavailable" (no camera /
// camera busy / anything else) so the UI can show the right message without string-
// matching browser-specific error names itself.
//
// Decode cadence is a deliberate ~4fps interval, not requestAnimationFrame: QR scanning
// doesn't benefit from 60fps attempts, and the jsQR fallback path does a full-frame
// canvas read + decode per tick — cheap at 4fps, battery-hostile at 60.
export async function startQrScanner(video, onDecoded) {
  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: "environment" },
      audio: false,
    });
  } catch (err) {
    const name = (err && err.name) || "";
    const e = new Error(name === "NotAllowedError" || name === "SecurityError" ? "Camera access was blocked" : "Camera isn't available");
    e.kind = name === "NotAllowedError" || name === "SecurityError" ? "denied" : "unavailable";
    throw e;
  }

  video.srcObject = stream;
  // iOS refuses to render an inline <video> without this attribute — without it the
  // stream tries to go fullscreen-native, which breaks the overlay layout entirely.
  video.setAttribute("playsinline", "true");
  video.muted = true;
  await video.play().catch(() => {
    // Autoplay rejection (rare here — the stream is muted and user-initiated) just means
    // the first few ticks see readyState < 2 and skip; not fatal.
  });

  // Prefer the native detector when it genuinely supports QR — feature-check the format
  // list rather than assuming, since BarcodeDetector can exist with other formats only.
  let detector = null;
  if (typeof window !== "undefined" && window.BarcodeDetector) {
    try {
      const formats = window.BarcodeDetector.getSupportedFormats
        ? await window.BarcodeDetector.getSupportedFormats()
        : ["qr_code"];
      if (formats.includes("qr_code")) detector = new window.BarcodeDetector({ formats: ["qr_code"] });
    } catch {
      detector = null; // fall through to jsQR
    }
  }

  let canvas = null;
  let ctx = null;
  let stopped = false;
  let busy = false; // detector.detect is async — never stack overlapping detects

  async function tick() {
    if (stopped || busy) return;
    busy = true;
    try {
      if (video.readyState >= 2) {
        if (detector) {
          const found = await detector.detect(video);
          if (!stopped && found && found.length && found[0].rawValue) onDecoded(found[0].rawValue);
        } else {
          const w = video.videoWidth;
          const h = video.videoHeight;
          if (w && h) {
            if (!canvas) {
              canvas = document.createElement("canvas");
              ctx = canvas.getContext("2d", { willReadFrequently: true });
            }
            canvas.width = w;
            canvas.height = h;
            ctx.drawImage(video, 0, 0, w, h);
            const img = ctx.getImageData(0, 0, w, h);
            const hit = jsQR(img.data, w, h);
            if (!stopped && hit && hit.data) onDecoded(hit.data);
          }
        }
      }
    } catch {
      // A single failed frame (detector hiccup, mid-teardown draw) is fine — keep going.
    } finally {
      busy = false;
    }
  }

  const interval = setInterval(tick, 250);

  return function stop() {
    stopped = true;
    clearInterval(interval);
    try {
      stream.getTracks().forEach((t) => t.stop());
    } catch {}
    try {
      video.srcObject = null;
    } catch {}
  };
}
